import { FC } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Medal } from 'lucide-react';
import { useGlobalRanking } from '@/hooks/useGlobalRanking';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';

export const GlobalRanking: FC = () => {
  const { ranking, isLoading } = useGlobalRanking();

  if (isLoading) {
    return (
      <Card className="p-6 space-y-4">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-primary/10 rounded w-1/3"></div>
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-16 bg-primary/5 rounded"></div>
            ))}
          </div>
        </div>
      </Card>
    );
  }

  const { topUsers, currentUser } = ranking || {};

  return (
    <Card className="p-6">
      <div className="flex items-center gap-3 mb-6">
        <Trophy className="w-6 h-6 text-primary" />
        <h2 className="text-xl font-bold">Ranking Global</h2>
      </div>

      {/* Podio (Top 3) */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        {/* Primer Lugar */}
        {topUsers?.[0] && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center"
          >
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-yellow-300 to-yellow-100 flex items-center justify-center mb-2">
              <Trophy className="w-10 h-10 text-yellow-600" />
            </div>
            <div className="flex items-center gap-1 mb-1">
              <span className="text-sm font-bold text-yellow-600">1ro</span>
            </div>
            <span className="text-sm font-bold truncate max-w-[120px] text-center">
              {topUsers[0].student_name}
            </span>
            <span className="text-xs text-muted-foreground">
              {topUsers[0].cerebrix} Cerebrix
            </span>
          </motion.div>
        )}

        {/* Segundo Lugar */}
        {topUsers?.[1] && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="flex flex-col items-center"
          >
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-gray-300 to-gray-100 flex items-center justify-center mb-2">
              <Medal className="w-8 h-8 text-gray-600" />
            </div>
            <div className="flex items-center gap-1 mb-1">
              <span className="text-sm font-bold text-gray-600">2do</span>
            </div>
            <span className="text-sm font-medium truncate max-w-[120px] text-center">
              {topUsers[1].student_name}
            </span>
            <span className="text-xs text-muted-foreground">
              {topUsers[1].cerebrix} Cerebrix
            </span>
          </motion.div>
        )}

        {/* Tercer Lugar */}
        {topUsers?.[2] && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="flex flex-col items-center"
          >
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-300 to-amber-100 flex items-center justify-center mb-2">
              <Medal className="w-8 h-8 text-amber-600" />
            </div>
            <div className="flex items-center gap-1 mb-1">
              <span className="text-sm font-bold text-amber-600">3ro</span>
            </div>
            <span className="text-sm font-medium truncate max-w-[120px] text-center">
              {topUsers[2].student_name}
            </span>
            <span className="text-xs text-muted-foreground">
              {topUsers[2].cerebrix} Cerebrix
            </span>
          </motion.div>
        )}
      </div>

      {/* Lista del 4 al 10 */}
      <div className="space-y-3">
        {topUsers?.slice(3).map((user, index) => (
          <motion.div
            key={user.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 * (index + 1) }}
            className={cn(
              "flex items-center gap-4 p-3 rounded-lg",
              user.id === currentUser?.id ? "bg-primary/5" : "hover:bg-muted/50"
            )}
          >
            <span className="w-6 text-center font-medium text-muted-foreground">
              {index + 4}
            </span>
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
              {user.current_badge_icon}
            </div>
            <div className="flex-1">
              <p className="font-medium">{user.student_name}</p>
              <p className="text-xs text-muted-foreground">Nivel {user.cerebrix_level}</p>
            </div>
            <div className="text-right">
              <p className="font-medium">{user.cerebrix}</p>
              <p className="text-xs text-muted-foreground">Cerebrix</p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Posición del usuario actual si no está en el top 10 */}
      {currentUser && !topUsers?.find(u => u.id === currentUser.id) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-6 p-4 rounded-lg bg-primary/5 border border-primary/10"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Tu posición actual</span>
            <span className="text-xs text-muted-foreground">
              Top {((currentUser.rank / (currentUser.rank + 10)) * 100).toFixed(1)}%
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="w-6 text-center font-medium text-muted-foreground">
              {currentUser.rank}
            </span>
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
              {currentUser.current_badge_icon}
            </div>
            <div className="flex-1">
              <p className="font-medium">{currentUser.student_name}</p>
              <p className="text-xs text-muted-foreground">Nivel {currentUser.cerebrix_level}</p>
            </div>
            <div className="text-right">
              <p className="font-medium">{currentUser.cerebrix}</p>
              <p className="text-xs text-muted-foreground">Cerebrix</p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Texto explicativo */}
      <div className="mt-8 p-4 bg-blue-50 rounded-lg text-sm text-blue-700">
        <h3 className="font-semibold mb-2">¿Cómo funciona el ranking?</h3>
        <p className="mb-2">
          El ranking global muestra a los estudiantes más destacados de FinancieraMente. Tu posición se determina por la cantidad total de Cerebrix que has acumulado.
        </p>
        <p>
          ¡Sigue completando lecciones, jugando minijuegos y aprendiendo para subir en el ranking! Cada Cerebrix te acerca más a la cima. 🚀
        </p>
      </div>
    </Card>
  );
};
